import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { MatDividerModule } from '@angular/material/divider';
import { SearchInputModule } from '@data-terminal/ui-presentational/components';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { LoadingIndicatorComponentModule } from '@data-terminal/ui-presentational/loading-indicator';
import { catchError, debounceTime, distinctUntilChanged, Observable, of, switchMap, take, tap } from 'rxjs';
import { BatchInformationDialogService } from '@data-terminal/feature-dialogs/services/batch-information/batch-information-dialog.service';
import { MatDialogRef } from '@angular/material/dialog';
import { JobService } from '@data-terminal/feature-workstation-details/services/job/job.service';
import { valueWhitelistedValidator } from '@data-terminal/utils/form';
import { Job } from '@data-terminal/shared-models/job';

export interface JobSelectionForm {
    jobId: FormControl<string>;
}

@UntilDestroy()
@Component({
    imports: [
        CommonModule,
        MatButtonModule,
        MatInputModule,
        TranslateModule,
        MatDividerModule,
        SearchInputModule,
        MatAutocompleteModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        LoadingIndicatorComponentModule,
    ],
    selector: 'data-terminal-request-batch-information-dialog',
    templateUrl: './request-batch-information-dialog.component.html',
    styleUrls: ['./request-batch-information-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestBatchInformationDialogComponent implements OnInit {
    readonly #batchInformationDialogService = inject(BatchInformationDialogService);
    readonly #dialogRef = inject(MatDialogRef<RequestBatchInformationDialogComponent>);
    readonly #jobService = inject(JobService);

    readonly JOB_SUGGESTION_MIN_LENGTH = 3;

    jobIdSuggestions$: Observable<Job[]> = of([]);
    jobSelectionForm!: FormGroup<JobSelectionForm>;

    ngOnInit(): void {
        this.initJobSelectionForm();
        this.listenForJobIdSuggestions();
    }

    cancel(): void {
        this.#dialogRef.close();
    }

    requestBatchInformation(): void {
        this.#dialogRef.close();
        this.#batchInformationDialogService
            .requestBatchInformation(this.jobSelectionForm.controls.jobId.value)
            .subscribe();
    }

    private initJobSelectionForm(): void {
        this.jobSelectionForm = new FormGroup<JobSelectionForm>({
            jobId: new FormControl<string>('', {
                nonNullable: true,
                validators: [Validators.required, valueWhitelistedValidator([])],
            }),
        });
    }

    private listenForJobIdSuggestions(): void {
        this.jobIdSuggestions$ = this.jobIdFromControl.valueChanges.pipe(
            untilDestroyed(this),
            debounceTime(1000),
            distinctUntilChanged(),
            switchMap((jobId: string) => {
                if (jobId.length >= this.JOB_SUGGESTION_MIN_LENGTH) {
                    return this.#jobService.getJobList(jobId).pipe(
                        take(1),
                        catchError(() => of([]))
                    );
                } else {
                    return of([]);
                }
            }),
            tap((jobIdList) => this.updateJobIdValueAndValidity(jobIdList.map((job) => job.jobId)))
        );
    }

    private updateJobIdValueAndValidity(jobIdList: string[]): void {
        this.jobIdFromControl.setValidators([Validators.required, valueWhitelistedValidator(jobIdList)]);
        this.jobIdFromControl.updateValueAndValidity();
    }

    get jobIdFromControl(): FormControl {
        return this.jobSelectionForm.get('jobId') as FormControl;
    }
}
